import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import imageCompression from "browser-image-compression";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Camera, ChevronLeft, ChevronRight, CheckSquare, Download, GripVertical,
  ImagePlus, Square, Star, Trash2, Upload, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, API, getToken, vehicleFotoUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useDoubleConfirm } from "@/components/ConfirmProvider";

// Sprint 2C.2 — Foto veicolo con categorie (Veicolo/Danni), selezione multipla,
// popup navigabile, download (singolo o ZIP), delete batch.

const CATEGORIE = [
  { key: "VEICOLO", label: "Foto veicolo", max: 32 },
  { key: "DANNI", label: "Imperfezioni/Danni", max: 16 },
];
const MAX_BY_CAT = Object.fromEntries(CATEGORIE.map((c) => [c.key, c.max]));
const COMPRESSION_OPTS = {
  maxSizeMB: 0.3,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  fileType: "image/jpeg",
};
const ALLOWED_TYPES = new Set(["image/jpeg", "image/jpg", "image/png"]);

function extractErr(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Errore imprevisto";
  if (typeof d === "string") return d;
  if (typeof d === "object") {
    if (d.code === "max_foto_reached") return `Limite massimo di ${d.limit} foto raggiunto per la categoria ${d.categoria}.`;
    if (d.code === "foto_format_invalid") return "Formato non valido. Solo JPEG e PNG.";
    if (d.code === "foto_too_large") return `Foto troppo grande (${Math.round((d.size_bytes || 0) / 1024)} KB).`;
    if (d.code === "reorder_mismatch") return "Lista di riordino non coerente con le foto della categoria.";
    if (d.code === "reorder_mixed_categorie") return "Il riordino non può mescolare categorie diverse.";
    if (d.code === "batch_delete_unknown_ids") return "Alcuni id non esistono più.";
    if (d.message) return d.message;
  }
  return JSON.stringify(d);
}

function SortablePhoto({ photo, vehicleId, isCover, canEdit, selected, onToggleSel, onOpen }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: photo.id, disabled: !canEdit });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
    zIndex: isDragging ? 50 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group rounded-lg overflow-hidden border bg-white shadow-sm cursor-pointer ${
        selected ? "border-blue-500 ring-2 ring-blue-300" : "border-slate-200"
      }`}
      data-testid={`foto-tile-${photo.categoria}-${photo.ord}`}
      onClick={() => onOpen(photo)}
    >
      {/* Drag handle */}
      {canEdit && (
        <div
          {...attributes}
          {...listeners}
          className="absolute top-1.5 left-1.5 z-10 p-1 rounded bg-white/85 backdrop-blur-sm border border-slate-200 text-slate-600 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition"
          title="Trascina per riordinare"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </div>
      )}

      {/* Selection checkbox */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleSel(photo.id); }}
        className={`absolute top-1.5 left-8 z-10 p-1 rounded-md border bg-white/90 ${
          selected ? "text-blue-600 border-blue-500" : "text-slate-400 border-slate-300"
        } opacity-100`}
        data-testid={`foto-sel-${photo.categoria}-${photo.ord}`}
        title={selected ? "Deseleziona" : "Seleziona"}
      >
        {selected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
      </button>

      {/* Badge numero d'ordine */}
      <div
        className="absolute bottom-9 left-1.5 z-10 px-1.5 py-0.5 rounded bg-slate-900/75 text-white text-[10px] font-bold tracking-wider"
        data-testid={`foto-ord-badge-${photo.categoria}-${photo.ord}`}
      >
        #{photo.ord + 1}
      </div>

      {/* Badge copertina (solo VEICOLO foto[0]) */}
      {isCover && (
        <div
          className="absolute top-1.5 right-1.5 z-10 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1 shadow-sm"
          data-testid="foto-badge-copertina"
        >
          <Star className="h-3 w-3 fill-current" />
          Copertina
        </div>
      )}

      <img
        src={vehicleFotoUrl(vehicleId, photo.id)}
        alt={photo.filename || `Foto ${photo.ord + 1}`}
        className="w-full aspect-[4/3] object-cover bg-slate-100 select-none"
        draggable={false}
        loading="lazy"
      />

      <div className="flex items-center justify-between px-2 py-1 bg-slate-50 text-[11px] text-slate-600 border-t border-slate-200">
        <span className="truncate max-w-[120px]" title={photo.filename}>{photo.filename}</span>
      </div>
    </div>
  );
}

// Lightbox popup
function Lightbox({ vehicleId, items, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  if (index < 0 || index >= items.length) return null;
  const p = items[index];
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
      data-testid="foto-lightbox"
    >
      {/* Close */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
        data-testid="foto-lightbox-close"
        title="Chiudi (Esc)"
      >
        <X className="h-6 w-6" />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-white/10 text-white text-sm font-medium"
           data-testid="foto-lightbox-counter">
        {index + 1} di {items.length}
      </div>

      {/* Prev */}
      {items.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-4 p-3 rounded-full bg-white/10 text-white hover:bg-white/25"
          data-testid="foto-lightbox-prev"
          title="Precedente (←)"
        >
          <ChevronLeft className="h-7 w-7" />
        </button>
      )}

      {/* Image */}
      <img
        src={vehicleFotoUrl(vehicleId, p.id)}
        alt={p.filename || ""}
        className="max-h-[88vh] max-w-[88vw] object-contain"
        onClick={(e) => e.stopPropagation()}
        data-testid="foto-lightbox-img"
      />

      {/* Next */}
      {items.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-4 p-3 rounded-full bg-white/10 text-white hover:bg-white/25"
          data-testid="foto-lightbox-next"
          title="Successiva (→)"
        >
          <ChevronRight className="h-7 w-7" />
        </button>
      )}
    </div>
  );
}

export default function VehicleFotoTab({ vehicleId, canEdit = true, onCountChange }) {
  const confirm = useDoubleConfirm();
  const [activeCat, setActiveCat] = useState("VEICOLO");
  const [allFoto, setAllFoto] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0, label: "" });
  const [selection, setSelection] = useState(new Set());
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const onCountChangeRef = useRef(onCountChange);
  useEffect(() => { onCountChangeRef.current = onCountChange; }, [onCountChange]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fotoByCat = useMemo(() => {
    const m = { VEICOLO: [], DANNI: [] };
    (allFoto || []).forEach((f) => {
      const c = f.categoria || "VEICOLO";
      if (m[c]) m[c].push(f);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.ord || 0) - (b.ord || 0)));
    return m;
  }, [allFoto]);

  const current = fotoByCat[activeCat] || [];
  const max = MAX_BY_CAT[activeCat];
  const slotsLeft = max - current.length;
  const reachedLimit = slotsLeft <= 0;
  // Selection scoped to active category
  const selInCat = useMemo(
    () => current.filter((p) => selection.has(p.id)).map((p) => p.id),
    [current, selection],
  );
  const allSelected = current.length > 0 && selInCat.length === current.length;

  const load = useCallback(async () => {
    if (!vehicleId) return;
    setLoading(true);
    try {
      const r = await api.get(`/vehicles/${vehicleId}`);
      const f = r.data?.foto || [];
      setAllFoto(f);
      onCountChangeRef.current?.(f.length);
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelection(new Set()); setLightboxIdx(-1); }, [activeCat]);

  // Upload
  const compressAndUpload = async (rawFile, categoria) => {
    if (!ALLOWED_TYPES.has((rawFile.type || "").toLowerCase())) {
      throw new Error(`Formato non valido (${rawFile.type || "?"}). Solo JPEG e PNG.`);
    }
    let blob;
    try { blob = await imageCompression(rawFile, COMPRESSION_OPTS); }
    catch { blob = rawFile; }
    const ext = "jpg";
    const cleanName = (rawFile.name || `foto.${ext}`).replace(/\.[^./\\]+$/, `.${ext}`);
    const fd = new FormData();
    fd.append("file", blob, cleanName);
    const r = await api.post(`/vehicles/${vehicleId}/foto?categoria=${categoria}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
  };

  const onFilesPicked = async (filesList) => {
    if (!canEdit) return;
    const files = Array.from(filesList || []).filter((f) =>
      ALLOWED_TYPES.has((f.type || "").toLowerCase()),
    );
    if (files.length === 0) {
      toast.error("Nessun file JPEG/PNG selezionato.");
      return;
    }
    if (slotsLeft <= 0) {
      toast.error(`Hai raggiunto il limite di ${max} foto per ${activeCat}.`);
      return;
    }
    const toUpload = files.slice(0, slotsLeft);
    if (files.length > slotsLeft) {
      toast.warning(`Solo ${slotsLeft} foto verranno caricate (limite ${max}).`);
    }
    setUploading(true);
    setUploadProgress({ done: 0, total: toUpload.length, label: toUpload[0]?.name || "" });
    let ok = 0;
    for (let i = 0; i < toUpload.length; i++) {
      const f = toUpload[i];
      setUploadProgress({ done: i, total: toUpload.length, label: f.name });
      try {
        const created = await compressAndUpload(f, activeCat);
        setAllFoto((prev) => [...prev, created]);
        ok += 1;
      } catch (e) {
        toast.error(`${f.name}: ${extractErr(e)}`);
      }
    }
    setUploading(false);
    setUploadProgress({ done: 0, total: 0, label: "" });
    if (ok > 0) toast.success(`${ok} foto caricat${ok === 1 ? "a" : "e"}.`);
    // refresh per ricavare ord coerenti
    load();
  };

  const onDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); setDragActive(false); };
  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (!canEdit) return;
    onFilesPicked(e.dataTransfer?.files);
  };

  const toggleSel = (id) => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelAll = () => {
    if (allSelected) {
      setSelection((prev) => {
        const next = new Set(prev);
        current.forEach((p) => next.delete(p.id));
        return next;
      });
    } else {
      setSelection((prev) => {
        const next = new Set(prev);
        current.forEach((p) => next.add(p.id));
        return next;
      });
    }
  };

  const handleDownload = async () => {
    if (selInCat.length === 0) {
      toast.error("Seleziona almeno una foto.");
      return;
    }
    try {
      const token = getToken() || "";
      const r = await fetch(`${API}/vehicles/${vehicleId}/foto/download`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ ids: selInCat }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const cd = r.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename = (m && m[1]) || (selInCat.length > 1 ? "foto.zip" : "foto.jpg");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Scaricat${selInCat.length === 1 ? "a 1 foto" : "e " + selInCat.length + " foto"}.`);
    } catch (e) {
      toast.error(`Errore download: ${e.message || e}`);
    }
  };

  const handleDeleteSel = async () => {
    if (selInCat.length === 0) {
      toast.error("Seleziona almeno una foto.");
      return;
    }
    const ok = await confirm({
      title: `Eliminare ${selInCat.length} foto selezionate?`,
      description: "L'azione rimuove definitivamente le foto.",
      confirmText: "SI, elimina",
      cancelText: "NO, annulla",
    });
    if (!ok) return;
    try {
      await api.post(`/vehicles/${vehicleId}/foto/delete-batch`, { ids: selInCat });
      setSelection((prev) => {
        const next = new Set(prev);
        selInCat.forEach((id) => next.delete(id));
        return next;
      });
      toast.success(`Eliminat${selInCat.length === 1 ? "a 1 foto" : "e " + selInCat.length + " foto"}.`);
      load();
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = current.findIndex((p) => p.id === active.id);
    const newIdx = current.findIndex((p) => p.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(current, oldIdx, newIdx).map((p, i) => ({ ...p, ord: i }));
    // Merge in allFoto
    setAllFoto((prev) => {
      const others = prev.filter((p) => p.categoria !== activeCat);
      return [...others, ...reordered];
    });
    try {
      await api.patch(`/vehicles/${vehicleId}/foto/order`, {
        order: reordered.map((p) => p.id),
        categoria: activeCat,
      });
      if (activeCat === "VEICOLO" && newIdx === 0) toast.success("Copertina aggiornata.");
    } catch (e) {
      toast.error(extractErr(e));
      load(); // rollback via reload
    }
  };

  const openLightbox = (photo) => {
    const i = current.findIndex((p) => p.id === photo.id);
    if (i >= 0) setLightboxIdx(i);
  };

  return (
    <Card className="p-4 sm:p-6" data-testid="vehicle-foto-tab">
      {/* Sub-tabs categoria */}
      <div className="flex items-center gap-2 mb-4 border-b border-slate-200" data-testid="foto-sub-tabs">
        {CATEGORIE.map((c) => {
          const count = fotoByCat[c.key]?.length || 0;
          const active = activeCat === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setActiveCat(c.key)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                active
                  ? "border-blue-500 text-blue-700"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`foto-tab-${c.key}`}
            >
              {c.label}
              <span className="ml-2 text-xs text-slate-500 font-normal">({count}/{c.max})</span>
            </button>
          );
        })}
      </div>

      {/* Header azioni vista corrente */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Camera className="h-5 w-5 text-slate-600" />
          <div>
            <div className="font-semibold text-slate-900">
              {CATEGORIE.find((c) => c.key === activeCat)?.label}
            </div>
            <div className="text-xs text-slate-500">
              Tot Foto <span className="font-semibold text-slate-700" data-testid="foto-count-tot">{current.length}</span> di {max}
              {activeCat === "VEICOLO" && current.length > 0 && (
                <> · La prima è la copertina · Trascina per riordinare</>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {current.length > 0 && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={toggleSelAll}
              data-testid="foto-sel-all-btn"
              className="h-9"
            >
              {allSelected ? <Square className="h-4 w-4 mr-1.5" /> : <CheckSquare className="h-4 w-4 mr-1.5" />}
              {allSelected ? "Deseleziona tutto" : "Seleziona tutto"}
            </Button>
          )}
          {selInCat.length > 0 && (
            <>
              <span className="text-xs text-blue-700 font-semibold" data-testid="foto-sel-count">
                {selInCat.length} selezionat{selInCat.length === 1 ? "a" : "e"}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleDownload}
                data-testid="foto-download-btn"
                className="h-9 border-blue-500 text-blue-700 hover:bg-blue-50"
              >
                <Download className="h-4 w-4 mr-1.5" /> Scarica
              </Button>
              {canEdit && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleDeleteSel}
                  data-testid="foto-delete-sel-btn"
                  className="h-9 border-rose-500 text-rose-700 hover:bg-rose-50"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" /> Elimina
                </Button>
              )}
            </>
          )}
          {canEdit && (
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || reachedLimit}
              className="h-9"
              data-testid="foto-upload-btn"
            >
              <ImagePlus className="h-4 w-4 mr-1.5" />
              Carica foto
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,.jpg,.jpeg,.png"
            multiple
            className="hidden"
            onChange={(e) => { onFilesPicked(e.target.files); e.target.value = ""; }}
            data-testid="foto-file-input"
          />
        </div>
      </div>

      {/* Drop zone */}
      {canEdit && (
        <div
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={`mb-4 border-2 border-dashed rounded-lg px-4 py-5 text-center transition-colors ${
            dragActive ? "border-blue-500 bg-blue-50"
            : reachedLimit ? "border-slate-200 bg-slate-50 opacity-60"
            : "border-slate-300 bg-slate-50 hover:border-slate-400"
          }`}
          data-testid="foto-dropzone"
        >
          <Upload className="h-5 w-5 text-slate-400 mx-auto mb-1.5" />
          <div className="text-sm text-slate-700">
            {reachedLimit
              ? `Limite di ${max} foto raggiunto per la categoria. Elimina prima qualche foto.`
              : "Trascina qui le foto oppure clicca \"Carica foto\""}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            Compressione automatica (~300 KB · 1920 px lato lungo) · JPEG/PNG
          </div>
        </div>
      )}

      {uploading && (
        <div className="mb-4 px-3 py-2 rounded border bg-blue-50 border-blue-200 text-sm flex items-center gap-2"
             data-testid="foto-upload-progress">
          <span className="inline-block h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
          <span className="text-blue-800 font-medium">
            Caricamento {uploadProgress.done + 1}/{uploadProgress.total}…
          </span>
          <span className="text-blue-700 truncate">{uploadProgress.label}</span>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-lg bg-slate-100 aspect-[4/3] animate-pulse" />
          ))}
        </div>
      ) : current.length === 0 ? (
        <div className="text-center py-10 text-slate-500" data-testid="foto-empty">
          <Camera className="h-10 w-10 mx-auto mb-2 text-slate-300" />
          <div className="font-medium text-slate-700">Nessuna foto in questa categoria</div>
          <div className="text-sm mt-1">
            {activeCat === "VEICOLO"
              ? "Aggiungi la prima foto: diventerà la copertina del veicolo."
              : "Carica le foto delle imperfezioni / dei danni."}
          </div>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={current.map((p) => p.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="foto-grid">
              {current.map((p) => (
                <SortablePhoto
                  key={p.id}
                  photo={p}
                  vehicleId={vehicleId}
                  isCover={activeCat === "VEICOLO" && p.ord === 0}
                  canEdit={canEdit}
                  selected={selection.has(p.id)}
                  onToggleSel={toggleSel}
                  onOpen={openLightbox}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Lightbox */}
      {lightboxIdx >= 0 && current.length > 0 && (
        <Lightbox
          vehicleId={vehicleId}
          items={current}
          index={Math.min(lightboxIdx, current.length - 1)}
          onClose={() => setLightboxIdx(-1)}
          onPrev={() => setLightboxIdx((i) => (i <= 0 ? current.length - 1 : i - 1))}
          onNext={() => setLightboxIdx((i) => (i >= current.length - 1 ? 0 : i + 1))}
        />
      )}
    </Card>
  );
}
